import { Card, CardBody } from '@statsbomb/kitbag-components';
import { MetricValueMode } from '@statsbomb/parachute-types';
import { useAtomValue, useSetAtom } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { Suspense, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SCOPE_CONTEXT_FILTER_KEYS, TEAM_SQUAD_FILTER_SOURCE } from '@/analytics/snowplow/filterTracking';
import { useTrackFilterChanges } from '@/analytics/snowplow/hooks/useTrackFilterChanges';
import { teamSquadDefaultColumnsAtom } from '@/atoms/dataLocker/player/table';
import { playerLinkAtom, playerPreviewLink } from '@/atoms/navigation';
import { tableColumnEntitiesAtom } from '@/atoms/queries/tableColumns';
import { teamSquadFilterParamsAtom } from '@/atoms/queries/team/teamAggData';
import { userNavOverridesAtom } from '@/atoms/queries/user';
import { metricValueModeAtom, orderByAtom, orderDirectionAtom } from '@/atoms/table';
import { unwrappedSelectedTeamNameAtom } from '@/atoms/team/info';
import { TableDataControls } from '@/components/controls/TableDataControls';
import { PageTitle } from '@/components/head/PageTitle';
import { TeamLayoutWithFilters } from '@/components/layout/team/TeamLayoutWithFilters';
import { OnboardingSettingsButton } from '@/components/onboarding/OnboardingSettingsButton';
import { AppearanceSettingsPanel } from '@/components/settings/AppearanceSettingsPanel';
import { ScopedTableWithDefaultColumns } from '@/components/tables/ScopedTableWithDefaultColumns';
import { TeamSquadTableWithPagination } from '@/components/tables/TeamSquadTable';
import { useCsvDownload } from '@/hooks/exporting/useCsvDownload';
import { useEntityPageTitle } from '@/hooks/useEntityPageTitle';
import { useTableColumns } from '@/hooks/useTableColumns';
import { playersNormalisedAggregatesUrl, playersTotalAggregatesUrl } from '@/query/url';
import { SORT_DIRECTION } from '@/types/ordering';
import { TableId } from '@/types/table';

const TeamSquadPageTable = () => {
  const setOrderTableBy = useSetAtom(orderByAtom);
  const setOrderDirection = useSetAtom(orderDirectionAtom);

  // Using useLayoutEffect to prevent instances where the table briefly
  // renders without default ordering when navigating via the tabs
  useLayoutEffect(() => {
    // Set squad page default ordering
    setOrderDirection(SORT_DIRECTION.DESC);
    setOrderTableBy('aggregates.minutes_played');
  }, []);

  return <TeamSquadTableWithPagination />;
};

const SetLinkBehaviour = () => {
  const isClassicTeamXp = useAtomValue(userNavOverridesAtom).team;
  const setPlayerLink = useSetAtom(playerLinkAtom);
  // useLayoutEffect (not useEffect) so the scoped link is set before paint
  useLayoutEffect(() => {
    if (!isClassicTeamXp) {
      setPlayerLink(playerPreviewLink);
    }
  }, [isClassicTeamXp]);
  return null;
};

const CsvDownload = ({ pageTitle }: { pageTitle: string }) => {
  const teamSquadFilterParams = useAtomValue(teamSquadFilterParamsAtom);
  const metricValueMode = useAtomValue(metricValueModeAtom);
  const urlCreationFunction =
    metricValueMode === MetricValueMode.Per90 ? playersNormalisedAggregatesUrl : playersTotalAggregatesUrl;
  useCsvDownload(pageTitle, urlCreationFunction(teamSquadFilterParams), { includeOnDemandMetrics: true });
  return null;
};

const TableWithControls = () => {
  const { t } = useTranslation('navigation');
  useTableColumns(['player', 'player_metric', 'player_total_metric']);

  return (
    <Card>
      <TableDataControls heading={t(`secondary.squad`)}>
        <OnboardingSettingsButton />
      </TableDataControls>
      <CardBody>
        <TeamSquadPageTable />
      </CardBody>
    </Card>
  );
};

export const TeamSquadPage = () => {
  const { t } = useTranslation('navigation');
  const teamName = useAtomValue(unwrappedSelectedTeamNameAtom);
  const pageTitle = useEntityPageTitle(t('secondary.squad'), teamName);
  useTrackFilterChanges(SCOPE_CONTEXT_FILTER_KEYS, TEAM_SQUAD_FILTER_SOURCE);

  return (
    <ScopeProvider atoms={[tableColumnEntitiesAtom, playerLinkAtom]}>
      <Suspense fallback={null}>
        <SetLinkBehaviour />
      </Suspense>
      <PageTitle>{pageTitle}</PageTitle>
      {/* NOTE - TeamLayoutWithFilters needs to be inside ScopedTableWithDefaultColumns so that the
          drawer's DataFormatToggle can reset the scoped pageAtom when the user switches modes. */}
      <ScopedTableWithDefaultColumns defaultColumnsAtom={teamSquadDefaultColumnsAtom} tableId={TableId.TEAM_SQUAD}>
        <TeamLayoutWithFilters settingsPanel={<AppearanceSettingsPanel />}>
          <CsvDownload pageTitle={pageTitle} />
          <TableWithControls />
        </TeamLayoutWithFilters>
      </ScopedTableWithDefaultColumns>
    </ScopeProvider>
  );
};
